<template>
  <div
    class="bg-white dark:bg-slate-900 flex justify-between items-center py-2 px-4 border-b border-slate-50 dark:border-slate-800/50 flex-col md:flex-row"
  >
    <div
      class="flex-1 w-100 flex flex-col md:flex-row items-center justify-center"
    >
      <div
        class="flex justify-center items-center mr-4 rtl:mr-0 rtl:ml-4 min-w-0"
      >
        <back-button
          v-if="showBackButton"
          :back-url="backButtonUrl"
          class="ltr:ml-0 rtl:mr-0 rtl:ml-4"
        />
        <Thumbnail
          :src="currentContact.thumbnail"
          :badge="inboxBadge"
          :username="currentContact.name"
          :status="currentContact.availability_status"
        />
        <div class="items-start flex flex-col ml-2 rtl:ml-0 rtl:mr-2 min-w-0">
          <woot-button
            variant="link"
            color-scheme="secondary"
            class="overflow-hidden whitespace-nowrap text-ellipsis"
            @click.prevent="
              !isSpecialRoute ? $emit('contact-panel-toggle') : null
            "
          >
            <h3
              class="font-color text-base inline-flex items-center gap-0.5 leading-tight capitalize m-0 p-0 text-ellipsis overflow-hidden whitespace-nowrap text-slate-900 dark:text-slate-100 min-w-0"
            >
              <span
                class="dark:text-slate-50 truncate min-w-0 text-ellipsis overflow-hidden whitespace-nowrap"
              >
                {{ currentContact.name }}
              </span>
              <lead-quality-crown
                v-if="accountId"
                :account-id="accountId"
                :metadata="currentContact.lead_quality_metadata"
              />
              <lead-response-watch
                class="flex-shrink-0"
                :waiting-since-unix="headerWatchData.unix"
                :force-red="headerWatchData.forceRed"
              />
              <fluent-icon
                v-if="!isHMACVerified"
                v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
                size="14"
                class="text-yellow-600 dark:text-yellow-500 my-0 mx-0.5 flex-shrink-0"
                icon="warning"
              />
            </h3>
          </woot-button>
          <div
            class="conversation--header--actions items-center flex text-xs gap-2 text-ellipsis overflow-hidden whitespace-nowrap"
          >
            <inbox-name v-if="hasMultipleInboxes" :inbox="inbox" />
            <!-- <span
              v-if="isSnoozed"
              class="font-medium text-yellow-600 dark:text-yellow-500"
            >
              {{ snoozedDisplayText }}
            </span> -->
            <!-- <woot-button
              class="p-0"
              size="small"
              variant="link"
              @click="$emit('contact-panel-toggle')"
            >
              {{ contactPanelToggleText }}
            </woot-button> -->
            <!-- <span class="status"> {{ activeStatus }} </span> -->
            <span class="channel">
              <fluent-icon
                icon="messanger"
                size="10"
                :viewBox="'0 0 512 512'"
                class="channel-position"
              />
              {{ channelName }}
            </span>
          </div>
        </div>
      </div>
      <div
        class="header-actions-wrap items-center flex flex-row flex-grow justify-end mt-3 lg:mt-0"
        :class="{ 'justify-end': isContactPanelOpen }"
      >
        <more-actions :conversation-id="currentChat.id" />
      </div>
    </div>
  </div>
</template>
<script>
import { hasPressedAltAndOKey } from 'shared/helpers/KeyboardHelpers';
import { mapGetters } from 'vuex';
import agentMixin from '../../../mixins/agentMixin.js';
import BackButton from '../BackButton';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import inboxMixin from 'shared/mixins/inboxMixin';
import InboxName from '../InboxName';
import MoreActions from './MoreActions';
import Thumbnail from '../Thumbnail';
import LeadQualityCrown from 'dashboard/components/LeadQualityCrown.vue';
import LeadResponseWatch from 'dashboard/components/widgets/LeadResponseWatch.vue';
import { effectiveWaitingSinceUnixForConversation } from 'dashboard/helper/leadResponseWatch';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { conversationReopenTime } from 'dashboard/helper/snoozeHelpers';

export default {
  components: {
    BackButton,
    InboxName,
    MoreActions,
    Thumbnail,
    LeadQualityCrown,
    LeadResponseWatch,
  },
  mixins: [inboxMixin, agentMixin, eventListenerMixins],
  props: {
    chat: {
      type: Object,
      default: () => {},
    },
    isContactPanelOpen: {
      type: Boolean,
      default: false,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    activeStatus: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      baseDocumentTitle: null,
    };
  },
  mounted() {
    this.updateDocumentTitle(this.currentContact?.name);
  },
  beforeDestroy() {
    this.restoreDocumentTitle();
  },
  watch: {
    'currentContact.name'(newName) {
      this.updateDocumentTitle(newName);
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxAssignableAgents/getUIFlags',
      currentChat: 'getSelectedChat',
      accountId: 'getCurrentAccountId',
    }),
    chatMetadata() {
      return this.chat.meta;
    },
    backButtonUrl() {
      const {
        params: { accountId, inbox_id: inboxId, label, teamId },
        name,
      } = this.$route;
      return conversationListPageURL({
        accountId,
        inboxId,
        label,
        teamId,
        conversationType: name === 'conversation_mentions' ? 'mention' : '',
      });
    },
    isHMACVerified() {
      if (!this.isAWebWidgetInbox) {
        return true;
      }
      return this.chatMetadata.hmac_verified;
    },
    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.chat.meta.sender.id
      );
    },
    isSnoozed() {
      return this.currentChat.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    snoozedDisplayText() {
      const { snoozed_until: snoozedUntil } = this.currentChat;
      if (snoozedUntil) {
        return `${this.$t(
          'CONVERSATION.HEADER.SNOOZED_UNTIL'
        )} ${conversationReopenTime(snoozedUntil)}`;
      }
      return this.$t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
    },
    contactPanelToggleText() {
      return `${
        this.isContactPanelOpen
          ? this.$t('CONVERSATION.HEADER.CLOSE')
          : this.$t('CONVERSATION.HEADER.OPEN')
      } ${this.$t('CONVERSATION.HEADER.DETAILS')}`;
    },
    inbox() {
      const { inbox_id: inboxId } = this.chat;
      const storeInbox = this.$store.getters['inboxes/getInbox'](inboxId);
      // When an agent is not a collaborator on this inbox but is allowed to view
      // the conversation (e.g. they are the contact's assignee), the inbox is not
      // present in their store and +getInbox+ returns +{}+. Falling back to the
      // conversation payload (+meta.inbox_name+ + +meta.channel+) keeps the
      // channel icon, badge and label consistent with the admin UI instead of
      // the generic 'chat' icon.
      if (storeInbox && storeInbox.id) return storeInbox;

      const metaChannel = this.chat?.meta?.channel || '';
      const channelLabel = metaChannel.split('::')[1] || metaChannel;
      return {
        id: inboxId,
        name: this.chat?.meta?.inbox_name || channelLabel,
        channel_type: metaChannel,
      };
    },
    hasMultipleInboxes() {
      return this.$store.getters['inboxes/getInboxes'].length > 1;
    },
    channelName() {
      return (
        this.currentChat.meta?.channel?.split('::')[1] ||
        this.currentChat.meta.channel
      );
    },
    isSpecialRoute() {
      const { path, query } = this.$route;

      // Updated regex to match "/app/accounts/{id}/contacts/{contactId}"
      const specialRoutePattern = /^\/app\/accounts\/\d+\/contacts\/\d+$/;

      return specialRoutePattern.test(path) && !query.page; // Exclude routes with `page` query parameter
    },
    currentChatChannelType() {
      const inboxId = this.currentChat?.inbox_id;
      if (!inboxId) return null;
      const inbox = this.$store.getters['inboxes/getInbox'](inboxId);
      return inbox?.channel_type || null;
    },
    headerWatchData() {
      const result = effectiveWaitingSinceUnixForConversation(
        this.currentChat,
        null,
        this.currentChatChannelType,
        { store: this.$store, accountId: this.accountId }
      );
      if (result == null) return { unix: null, forceRed: false };
      if (typeof result === 'object') return result;
      return { unix: result, forceRed: false };
    },
  },

  methods: {
    handleKeyEvents(e) {
      if (hasPressedAltAndOKey(e)) {
        this.$emit('contact-panel-toggle');
      }
    },
    updateDocumentTitle(contactName) {
      if (contactName) {
        const currentTitle = document.title;

        if (currentTitle.startsWith(`${contactName} - `)) {
          return;
        }

        const dashIndex = currentTitle.indexOf(' - ');
        const existingPart =
          dashIndex !== -1
            ? currentTitle.substring(dashIndex + 3)
            : currentTitle;

        this.baseDocumentTitle = existingPart;
        document.title = `${contactName} - ${existingPart}`;
      }
    },
    restoreDocumentTitle() {
      if (this.baseDocumentTitle) {
        document.title = this.baseDocumentTitle;
        this.baseDocumentTitle = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.status {
  color: red;
  background-color: #f6d6d6;
  font-size: 12px;
  margin: 0px 5px;
  padding: 0px 5px 3px 5px;
  border-radius: 5px;
}
.channel {
  font-weight: 500;
  display: contents;
  color: gray;
}
.conversation--header--actions {
  :deep(.inbox--name) {

    @apply m-0;
  }
}
.font-color{
  color: #464646 !important;
}
.channel-position{
  margin-top: 4px;
}
</style>
